import { TDraft, THeading, TVersion } from '../types';

/**
 * Toggles a heading (and its subtree, if enabled) in the selected/autoDisabled sets.
 *
 * - If `headingId` is already selected, this removes it (and all its children) from both sets.
 * - If `headingId` is not selected, this adds it (and all its children, if enabled) to `selectedHeadingIds`,
 *   while marking children as autoDisabled.
 *
 * @param version The current document/file version, containing all headings.
 * @param headingId The ID of the heading to toggle.
 * @param selectedHeadingIds The current set of selected headings.
 * @param autoDisabledIds The current set of auto-disabled headings.
 * @param enableAutoChildrenSelection Whether to recursively select/deselect all child headings.
 * @returns A tuple of [updatedSelectedHeadingIds, updatedAutoDisabledIds].
 */
const toggleHeadingHelper = (
  version: TDraft | TVersion | undefined,
  headingId: string,
  selectedHeadingIds: ReadonlySet<string>,
  autoDisabledIds: ReadonlySet<string>,
  enableAutoChildrenSelection: boolean = false
): [Set<string>, Set<string>] => {
  const newSelected = new Set(selectedHeadingIds);
  const newAutoDisabled = new Set(autoDisabledIds);

  if (!version) {
    console.error('Version not found');
    return [newSelected, newAutoDisabled];
  }

  const heading = version.headings.find((h: THeading) => h.id === headingId);
  if (!heading) {
    console.error('Heading not found:', headingId);
    return [newSelected, newAutoDisabled];
  }

  const wasSelected = newSelected.has(headingId);

  // If we're not selecting children, toggle only this heading and return.
  if (!enableAutoChildrenSelection) {
    if (wasSelected) {
      newSelected.delete(headingId);
      newAutoDisabled.delete(headingId);
    } else {
      newSelected.add(headingId);
    }
    return [newSelected, newAutoDisabled];
  }

  // Otherwise, select/deselect the entire subtree (any heading whose numbering starts with the target).
  for (const h of version.headings) {
    if (!h.numbering.startsWith(heading.numbering)) {
      continue;
    }
    if (wasSelected) {
      // Remove subtree
      newSelected.delete(h.id);
      newAutoDisabled.delete(h.id);
    } else {
      // Add subtree
      newSelected.add(h.id);
      if (h.id !== heading.id) {
        newAutoDisabled.add(h.id);
      }
    }
  }

  return [newSelected, newAutoDisabled];
};

export default toggleHeadingHelper;
